'use client';

import { createContext, useContext, type ReactNode, type RefObject } from 'react';

import type { useDaemonTriggerModal } from '../../hooks/useDaemonTriggerModal';
import type { useDaemonTriggerModalController } from '../../hooks/useDaemonTriggerModalController';
import type { BillingExposure } from '../../hooks/useBillingExposure';
import type { Daemon, DaemonTrigger, Project, Team } from '../../lib/api';
import type { AuthSession } from '../../lib/authSession';
import type { ThemeMode } from '../ThemeToggle';
import type { NavSectionDef } from './Sidebar';

type Translate = (key: string) => string;

type DaemonTriggerModalState = ReturnType<typeof useDaemonTriggerModal>;
type DaemonTriggerControllerState = ReturnType<typeof useDaemonTriggerModalController>;

export type AppShellContextValue = {
  session: AuthSession | null;

  selectedTeamId: string;
  selectedProjectId: string;
  currentTeam: Team | undefined;
  currentProject: Project | undefined;
  currentContextQuery: string;
  isProjectsLoadingView: boolean;
  /**
   * 워크스페이스가 아직 "어느 프로젝트인지"를 정하는 중.
   *
   * 이 동안 프로젝트 권한 게이트는 결론(접근 거부)을 그리면 안 된다 — 판정 대상이 없을 뿐
   * 권한이 없는 게 아니다. 해석이 끝났는데도 프로젝트가 없으면(0개 확정) `false`가 되어 안내로 넘어간다.
   */
  isWorkspaceProjectResolving: boolean;

  drawerId: string;
  isDrawerOpen: boolean;
  isSidebarCollapsed: boolean;
  onToggleSidebar: () => void;

  mainContentRef: RefObject<HTMLElement | null>;
  onPullToRefresh: () => Promise<void>;

  billingExposure: BillingExposure;

  navSections: NavSectionDef[];
  /** 프로젝트 permission 판정 전. 이 동안 내비는 결론(빈 메뉴)을 그리지 않는다. */
  isNavLoadingView: boolean;
  isAdminMode: boolean;

  runnableDaemons: Daemon[];
  daemonTriggerModal: DaemonTriggerModalState;
  daemonTriggerController: DaemonTriggerControllerState;
  mineActiveDaemonTriggers: DaemonTrigger[];
  activeDaemonTriggerCount: number;
  isDaemonTriggerDetailPanelOpen: boolean;
  isActivityDetailPanelOpen: boolean;
  isConsentBannerVisible: boolean;
  isPaginationVisible: boolean;

  onLogout: () => Promise<void>;
  onOpenSearch: () => void;
  onOpenSearchFromCommandPalette: () => void;
  onOpenProfile: () => void;
  onOpenSubscribe: () => void;
  onOpenSubscription: () => void;
  onOpenExecutionEnvironment: () => void;
  onOpenOnboardingGuideFromMenu: () => void;
  onOpenFeedback: () => void;
  onSelectThemeMode: (mode: ThemeMode) => void;

  tNav: Translate;
  tLayout: Translate;
  tCommon: Translate;
  tBilling: Translate;
};

const AppShellContext = createContext<AppShellContextValue | null>(null);

export const AppShellContextProvider = ({ value, children }: { value: AppShellContextValue; children: ReactNode }) => {
  return <AppShellContext.Provider value={value}>{children}</AppShellContext.Provider>;
};

export const useAppShellContext = (): AppShellContextValue => {
  const ctx = useContext(AppShellContext);
  if (!ctx) {
    throw new Error('useAppShellContext must be used within AppProviders');
  }
  return ctx;
};

/**
 * 셸 밖(셸리스 라우트·단위 테스트)에서도 쓸 수 있는 조회.
 *
 * 셸 신호가 있으면 참고하고, 없으면 없는 대로 동작해야 하는 공용 컴포넌트 전용이다.
 * 셸 안에서만 의미가 있는 화면은 `useAppShellContext`를 써서 잘못된 사용을 즉시 드러내라.
 */
export const useOptionalAppShellContext = (): AppShellContextValue | null => useContext(AppShellContext);
